/**
 * Cancelar a consulta marcada no SISAC (Fase 3): o motivo, a fila de espera e
 * a observação, como o "Cancelar horário" do SISAC.
 *
 * ── Só tela ──────────────────────────────────────────────────────────────
 *
 * O que a pessoa escolhe mora aqui; os motivos e o envio chegam prontos por
 * props, e o pedido sai por `aoCancelar`. A vaga e o paciente não são da tela:
 * o servidor usa os que gravou ao agendar.
 *
 * Enquanto o envio não volta, tudo trava: um segundo clique não libera nada a
 * mais, mas a pessoa veria a tela mudar debaixo dela.
 */

import { useState, type ReactNode } from 'react';
import { Botao, Campo, CampoArea, CampoEscolha, Interruptor } from '@/shared/ui';
import { Carregando } from '@/shared/ui/estados/estados';
import { Faixa } from '@/shared/ui/estados/faixa';
import {
  ESPERA_DO_SISAC,
  type ConsultaNaLista,
  type EstadoDoEnvio,
  type ListaDoFormulario,
} from '../lib/consultas-na-tela';
import './cancelar-consulta.css';

export function CancelarConsulta({
  consulta,
  paciente = null,
  motivos,
  envio,
  aoCancelar,
  aoVoltar,
  aoRecarregar,
}: {
  consulta: ConsultaNaLista;
  /** O paciente da vaga, "[código] - nome" (#1187); nulo enquanto lê ou sem vínculo. */
  paciente?: string | null;
  motivos: ListaDoFormulario;
  envio: EstadoDoEnvio;
  aoCancelar: (pedido: { motivo: string; filaDeEspera: boolean; observacao: string }) => void;
  aoVoltar: () => void;
  /** Outra pessoa mudou a consulta: relê e volta à lista. */
  aoRecarregar: () => void;
}): ReactNode {
  const [motivo, definirMotivo] = useState('');
  const [filaDeEspera, definirFilaDeEspera] = useState(false);
  const [observacao, definirObservacao] = useState('');
  const [tentou, definirTentou] = useState(false);
  const enviando = envio.situacao === 'enviando';
  const semMotivo = motivo.trim() === '';

  const cancelar = () => {
    definirTentou(true);
    if (!semMotivo && !enviando) {
      aoCancelar({ motivo: motivo.trim(), filaDeEspera, observacao });
    }
  };

  return (
    <form
      className="cancelar-consulta"
      aria-labelledby="cancelar-consulta-titulo"
      onSubmit={(evento) => {
        evento.preventDefault();
        cancelar();
      }}
    >
      <div className="cancelar-consulta__topo">
        <h3 className="cancelar-consulta__titulo" id="cancelar-consulta-titulo">
          Cancelar este agendamento?
        </h3>
      </div>
      {/* A consulta à vista antes de confirmar (#1187): é ela que sai da agenda. */}
      <dl className="cancelar-consulta__consulta">
        {paciente && (
          <div className="cancelar-consulta__linha">
            <dt>Paciente</dt>
            <dd>{paciente}</dd>
          </div>
        )}
        <div className="cancelar-consulta__linha">
          <dt>Dia e horário</dt>
          <dd>{consulta.quando ?? 'Sem data e hora'}</dd>
        </div>
        {consulta.medico && (
          <div className="cancelar-consulta__linha">
            <dt>Médico</dt>
            <dd>{consulta.medico.nome}</dd>
          </div>
        )}
        {consulta.especialidade && (
          <div className="cancelar-consulta__linha">
            <dt>Especialidade</dt>
            <dd>{consulta.especialidade.nome}</dd>
          </div>
        )}
        {consulta.codigoDaAgenda && (
          <div className="cancelar-consulta__linha">
            <dt>Agenda no SISAC</dt>
            <dd>{consulta.codigoDaAgenda}</dd>
          </div>
        )}
      </dl>
      <p className="cancelar-consulta__nota">
        A vaga volta a ficar livre no SISAC. O médico e a especialidade continuam na consulta, para
        agendar de novo.
      </p>

      {motivos.situacao === 'carregando' ? (
        <Carregando forma="linha" linhas={1} rotulo="Lendo os motivos no SISAC…" />
      ) : motivos.situacao === 'pronta' ? (
        <CampoEscolha
          id="cancelar-consulta-motivo"
          rotulo="Motivo"
          buscavel
          vazio={motivos.opcoes.length === 0 ? 'Nenhum motivo no SISAC' : 'Escolha'}
          valor={motivo}
          opcoes={motivos.opcoes.map((o) => ({ valor: o.valor, rotulo: o.rotulo }))}
          erro={tentou && semMotivo ? 'Escolha o motivo do cancelamento.' : undefined}
          desabilitado={enviando || motivos.opcoes.length === 0}
          aoEscolher={definirMotivo}
        />
      ) : (
        <Campo
          id="cancelar-consulta-motivo"
          rotulo="Motivo"
          ajuda={
            motivos.situacao === 'erro'
              ? `${motivos.recado} Digite o código do motivo, como o SISAC grava.`
              : 'O código do motivo, como o SISAC grava.'
          }
          autoComplete="off"
          value={motivo}
          erro={tentou && semMotivo ? 'Informe o motivo do cancelamento.' : undefined}
          disabled={enviando}
          onChange={(e) => definirMotivo(e.currentTarget.value)}
        />
      )}

      <Interruptor
        rotulo="Incluir na fila de espera"
        ligado={filaDeEspera}
        desabilitado={enviando}
        motivo={ESPERA_DO_SISAC}
        aoAlternar={() => definirFilaDeEspera((atual) => !atual)}
      />
      <CampoArea
        id="cancelar-consulta-observacao"
        rotulo="Observação (opcional)"
        rows={2}
        maxLength={500}
        value={observacao}
        disabled={enviando}
        onChange={(e) => definirObservacao(e.currentTarget.value)}
      />

      {envio.situacao === 'fora-do-ar' && (
        <Faixa tom="bloqueado" acao={{ rotulo: 'Tentar de novo', aoClicar: cancelar }}>
          O SISAC não respondeu. Nada foi cancelado. Tente de novo em instantes.
        </Faixa>
      )}
      {envio.situacao === 'conflito' && (
        <Faixa tom="atencao" acao={{ rotulo: 'Recarregar', aoClicar: aoRecarregar }}>
          Outra pessoa mudou esta consulta enquanto você cancelava. Nada foi cancelado.
        </Faixa>
      )}
      {envio.situacao === 'erro' && (
        <p className="cancelar-consulta__erro" role="alert">
          {envio.recado}
        </p>
      )}

      <div className="cancelar-consulta__acoes">
        <Botao
          variante="fantasma"
          desabilitado={enviando}
          motivo={ESPERA_DO_SISAC}
          onClick={aoVoltar}
        >
          Voltar
        </Botao>
        <Botao
          variante="perigo"
          type="submit"
          desabilitado={enviando}
          motivo={ESPERA_DO_SISAC}
          aria-busy={enviando || undefined}
        >
          {enviando ? 'Cancelando…' : 'Sim, cancelar agendamento'}
        </Botao>
      </div>
    </form>
  );
}
